import { useMemo, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useReadContract } from "wagmi";
import { formatEther } from "viem";
import { Link } from "react-router-dom";
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Input } from "@/components/input";
import { useBlockchain, useStats } from "@/providers/global-data.provider";
import { LRSNODE_V3_CONTRACT_CONFIG } from "@/configs/lrsnode.config";
import {
  RiCoinLine,
  RiLoader4Line,
  RiPulseLine,
  RiTimeLine,
} from "@remixicon/react";

const BLOCK_TIME_SECONDS = 13;
const LRSN_HOLDER_SHARE = 0.07;
const DAYS_PER_WEEK = 7;
const DAYS_PER_YEAR = 365;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;

/** LRS token amounts — always 4 decimal places */
const formatLrs = (value: number) =>
  Number.isFinite(value)
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })
    : "0.0000";

/** USD price of 1 LRS — 6 decimal places */
const formatLrsUsdPrice = (usdPerLrs: number) =>
  Number.isFinite(usdPerLrs)
    ? usdPerLrs.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
      })
    : "$0.000000";

const formatUsd = (value: number) =>
  Number.isFinite(value)
    ? value.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "$0.00";

const formatTime = (days: number) => {
  if (!Number.isFinite(days) || days <= 0) return "N/A";
  if (days < 1) return `${(days * HOURS_PER_DAY).toFixed(1)} hours`;
  if (days < DAYS_PER_WEEK) return `${days.toFixed(2)} days`;
  if (days < 30) return `${(days / DAYS_PER_WEEK).toFixed(2)} weeks`;
  return `${(days / 30).toFixed(2)} months`;
};

const PLAN_OPTIONS = [
  { id: 1, label: "Pro key" },
  { id: 2, label: "Lite key" },
] as const;

export default function LRSNodeRewardCalculatorPage() {
  const { currentBlockReward, currentBlock, isLoading, error } = useBlockchain();
  const { price: lrsPriceUsd } = useStats();
  const [nodeCountInput, setNodeCountInput] = useState("1");
  const [selectedPlanId, setSelectedPlanId] = useState(1);
  const selectedPlanLabel = useMemo(
    () =>
      PLAN_OPTIONS.find((plan) => plan.id === selectedPlanId)?.label ??
      `Plan #${selectedPlanId}`,
    [selectedPlanId]
  );

  const { data: planPriceWei, isLoading: isLoadingPlanPrice } = useReadContract({
    ...LRSNODE_V3_CONTRACT_CONFIG,
    functionName: "requiredWeiForPlan",
    args: [BigInt(selectedPlanId)],
  });

  const { data: currentKeyBuy, isLoading: isLoadingNodeSupply } = useReadContract({
    ...LRSNODE_V3_CONTRACT_CONFIG,
    functionName: "currentKeyBuy",
  });

  const nodeCount = Math.max(1, Number.parseInt(nodeCountInput || "1", 10) || 1);
  const nodePriceLrs = planPriceWei ? Number(formatEther(planPriceWei as bigint)) : 0;
  const totalNetworkNodes = Number(currentKeyBuy ?? 0n);

  const metrics = useMemo(() => {
    const blocksPerDay =
      (HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE) / BLOCK_TIME_SECONDS;
    const blocksPerWeek = blocksPerDay * DAYS_PER_WEEK;
    const blocksPerYear = blocksPerDay * DAYS_PER_YEAR;

    const holderRewardPerBlock = currentBlockReward * LRSN_HOLDER_SHARE;
    const rewardPerNodePerBlock =
      totalNetworkNodes > 0 ? holderRewardPerBlock / totalNetworkNodes : 0;

    const userRewardPerBlock = rewardPerNodePerBlock * nodeCount;
    const daily = userRewardPerBlock * blocksPerDay;
    const weekly = userRewardPerBlock * blocksPerWeek;
    const monthly = (userRewardPerBlock * blocksPerYear) / 12;
    const yearly = userRewardPerBlock * blocksPerYear;
    /** Six months of estimated rewards (Lite key horizon). */
    const sixMonth = (userRewardPerBlock * blocksPerYear) / 2;

    const totalInvestment = nodePriceLrs * nodeCount;
    const breakEvenDays = daily > 0 ? totalInvestment / daily : Number.POSITIVE_INFINITY;

    return {
      blocksPerDay,
      holderRewardPerBlock,
      rewardPerNodePerBlock,
      daily,
      weekly,
      monthly,
      yearly,
      sixMonth,
      totalInvestment,
      breakEvenDays,
    };
  }, [currentBlockReward, totalNetworkNodes, nodeCount, nodePriceLrs]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/lrsnode">LRSNode</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Reward calculator (estimates)</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <div>
                  <h1 className="text-3xl font-bold">LRSNode reward calculator</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Estimated rewards only — not a promise of future returns.
                  </p>
                </div>
              </div>

              <p className="text-muted-foreground mb-6 max-w-3xl">
                Uses live V3 node buy price, live current block reward, and a simple model that
                allocates 7% of block rewards to LRSN holders. All earnings and break-even
                projections below are <span className="font-medium text-foreground">estimates</span>,
                assuming 13-second block time and today&apos;s network size.
              </p>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <Card className="bg-sidebar border border-border lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <RiCoinLine className="size-4 text-[#FF8A3C]" />
                      Calculator inputs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">
                        Plan
                      </label>
                      <select
                        value={String(selectedPlanId)}
                        onChange={(e) =>
                          setSelectedPlanId(Number.parseInt(e.target.value, 10) || 1)
                        }
                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      >
                        {PLAN_OPTIONS.map((plan) => (
                          <option key={plan.id} value={String(plan.id)}>
                            {plan.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm text-muted-foreground mb-1 block">
                        Number of nodes
                      </label>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        value={nodeCountInput}
                        onChange={(e) => setNodeCountInput(e.target.value)}
                      />
                    </div>

                    <div className="rounded-lg border border-border p-3 bg-background/40">
                      <p className="text-xs text-muted-foreground mb-1">
                        Live V3 buy price ({selectedPlanLabel})
                      </p>
                      {isLoadingPlanPrice ? (
                        <p className="text-sm flex items-center gap-2">
                          <RiLoader4Line className="size-4 animate-spin" />
                          Loading...
                        </p>
                      ) : (
                        <p className="text-lg font-semibold text-[#FF8A3C]">
                          {formatLrs(nodePriceLrs)} LRS / node
                        </p>
                      )}
                    </div>

                    <div className="rounded-lg border border-border p-3 bg-background/40">
                      <p className="text-xs text-muted-foreground mb-1">Current block reward</p>
                      {isLoading ? (
                        <p className="text-sm flex items-center gap-2">
                          <RiLoader4Line className="size-4 animate-spin" />
                          Loading...
                        </p>
                      ) : error ? (
                        <p className="text-sm text-red-400">{error}</p>
                      ) : (
                        <p className="text-lg font-semibold">
                          {formatLrs(currentBlockReward)} LRS
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-sidebar border border-border lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <RiPulseLine className="size-4 text-[#FF8A3C]" />
                      Estimated network rewards
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-lg border border-border p-4 bg-background/30">
                        <p className="text-xs text-muted-foreground">Current block</p>
                        <p className="text-xl font-semibold text-[#FF8A3C]">
                          #{currentBlock.toLocaleString()}
                        </p>
                      </div>
                      <div className="rounded-lg border border-border p-4 bg-background/30">
                        <p className="text-xs text-muted-foreground">Total network nodes (V3)</p>
                        {isLoadingNodeSupply ? (
                          <p className="text-sm flex items-center gap-2 mt-1">
                            <RiLoader4Line className="size-4 animate-spin" />
                            Loading...
                          </p>
                        ) : (
                          <p className="text-xl font-semibold">
                            {totalNetworkNodes.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="rounded-lg border border-border p-4 bg-background/30">
                        <p className="text-xs text-muted-foreground">
                          Est. 7% holder pool / block
                        </p>
                        <p className="text-lg font-semibold">
                          {formatLrs(metrics.holderRewardPerBlock)} LRS
                        </p>
                      </div>
                      <div className="rounded-lg border border-border p-4 bg-background/30">
                        <p className="text-xs text-muted-foreground">
                          Est. reward per node / block
                        </p>
                        <p className="text-lg font-semibold">
                          {formatLrs(metrics.rewardPerNodePerBlock)} LRS
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Estimated weekly earnings</p>
                    <p className="text-2xl font-bold text-[#FF8A3C]">
                      {formatLrs(metrics.weekly)} LRS
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatUsd(metrics.weekly * lrsPriceUsd)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Estimated monthly earnings</p>
                    <p className="text-2xl font-bold text-[#FF8A3C]">
                      {formatLrs(metrics.monthly)} LRS
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatUsd(metrics.monthly * lrsPriceUsd)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-5">
                    {selectedPlanId === 1 ? (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Estimated yearly earnings
                        </p>
                        <p className="text-2xl font-bold text-[#FF8A3C]">
                          {formatLrs(metrics.yearly)} LRS
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatUsd(metrics.yearly * lrsPriceUsd)}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Estimated 6-month earnings
                        </p>
                        <p className="text-2xl font-bold text-[#FF8A3C]">
                          {formatLrs(metrics.sixMonth)} LRS
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatUsd(metrics.sixMonth * lrsPriceUsd)}
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
                <Card className="bg-sidebar border border-border">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">Estimated break-even time</p>
                    <p className="text-2xl font-bold text-[#FF8A3C]">
                      {formatTime(metrics.breakEvenDays)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-sidebar border border-border">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <RiTimeLine className="size-4 text-[#FF8A3C]" />
                    Assumptions
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-2">
                  <p className="text-foreground font-medium">
                    These estimates assume stable emissions and equal per-node share of the 7%
                    holder pool — actual rewards may differ.
                  </p>
                  <p>
                    Current LRS price (for USD conversion): {formatLrsUsdPrice(lrsPriceUsd)} / LRS
                  </p>
                  <p>
                    Block time: 13 seconds (
                    {Number.isFinite(metrics.blocksPerDay)
                      ? metrics.blocksPerDay.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 0,
                        })
                      : "0"}{" "}
                    blocks/day)
                  </p>
                  <p>Reward share to LRSN holders: 7% of current block reward (modeled)</p>
                  <p>
                    Network node divisor uses live V3 total purchased nodes from
                    <code className="ml-1">currentKeyBuy</code>.
                  </p>
                  <p>
                    Estimates are not guaranteed. Real rewards can vary with protocol updates,
                    node distribution, and on-chain emissions over time.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

